import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { HouseholdSummary } from "@/components/HouseholdSummary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ShieldCheck, MessageSquare, ArrowRight, ClipboardList, Stethoscope, Pill, Heart, Handshake, Phone, FileText, Download, Lock, Users,
} from "lucide-react";

const PRIORITY_CHOICES = [
  { key: "keep_provider", label: "Keeping my doctor" },
  { key: "rx_cost", label: "Prescription affordability" },
  { key: "grocery", label: "Grocery / food allowance" },
  { key: "part_b_reduction", label: "Part B premium reduction" },
  { key: "network", label: "Network / system continuity" },
  { key: "moop", label: "Lower out-of-pocket max" },
];

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="mt-6 rounded-2xl border-2 border-[#E5E5E0] bg-white p-5" data-testid={testid}>
      <h2 className="font-bold text-[#0B192C] flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-[#1E3E62]" />} {title}
      </h2>
      {children}
    </div>
  );
}

export default function MemberHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docName, setDocName] = useState("");
  const [medName, setMedName] = useState("");
  const [callCfg, setCallCfg] = useState(null);
  const [sunfireDocs, setSunfireDocs] = useState([]);
  const [access, setAccess] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqMsg, setReqMsg] = useState("");
  const [reqStatus, setReqStatus] = useState(null);
  const [reqBusy, setReqBusy] = useState(false);

  const load = () => api.get("/consumer/household/home").then((r) => setHome(r.data));

  useEffect(() => { load().finally(() => setLoading(false)); }, []);
  useEffect(() => {
    api.get("/consumer/household/sunfire-enrollments").then((r) => setSunfireDocs(r.data.documents || [])).catch(() => setSunfireDocs([]));
    api.get("/consumer/household/access").then((r) => setAccess(r.data)).catch(() => setAccess(null));
  }, []);
  useEffect(() => {
    api.get("/telephony/config").then((r) => setCallCfg(r.data)).catch(() => setCallCfg({ available: false }));
  }, []);

  const submitAccessRequest = async () => {
    setReqBusy(true); setReqStatus(null);
    try {
      const { data } = await api.post("/consumer/household/access-request", { message: reqMsg.trim() || null });
      setReqStatus({ ok: true, text: data.message });
      setReqMsg("");
    } catch (e) {
      setReqStatus({ ok: false, text: formatApiError(e?.response?.data?.detail) });
    } finally { setReqBusy(false); }
  };

  const addDoctor = async () => {
    const name = docName.trim();
    if (!name) return;
    try {
      await api.post("/consumer/household/doctors", { provider_name: name });
      setDocName(""); await load(); toast.success("Saved to your doctors");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const addMed = async () => {
    const val = medName.trim();
    if (!val) return;
    try {
      await api.post("/consumer/household/medications", { value: val });
      setMedName(""); await load(); toast.success("Saved to your prescriptions");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const addPriority = async (key) => {
    try { await api.post("/consumer/household/priorities", { key }); await load(); toast.success("Noted what matters to you"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const requestAdvisor = async () => {
    try { await api.post("/consumer/household/advisor"); await load(); toast.success("A licensed advisor can continue with your saved context."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const viewSunfire = async (d) => {
    try {
      const res = await api.get(`/consumer/household/sunfire-enrollments/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Couldn't open that document."); }
  };

  if (loading) {
    return <div className="p-6 sm:p-8 max-w-3xl mx-auto space-y-4"><Skeleton className="h-24 w-full rounded-2xl" /><Skeleton className="h-40 w-full rounded-2xl" /></div>;
  }

  const first = home?.first_name || (user?.name || "").split(" ")[0] || "there";
  const cov = home?.current_coverage || {};
  const covKnown = cov.state === "verified";
  const review = home?.review || {};
  const candidates = home?.plans_in_review || [];
  const doctors = home?.doctors || [];
  const meds = home?.medications || [];
  const whatMatters = home?.what_matters || [];
  const advisor = home?.advisor || {};
  const haveKeys = whatMatters.map((w) => w.key);

  return (
    <div className="p-5 sm:p-8 max-w-3xl mx-auto" data-testid="member-home">
      <PageHeader title={`My Nauticus, ${first}`} subtitle="Here's what you've saved and what Nauticus can help you work on. Use any part — nothing here is required." icon={ShieldCheck} />

      <div className="mt-4"><HouseholdSummary callCfg={callCfg} /></div>

      <p className="mt-1 text-xs text-[#6B7280]" data-testid="member-privacy-note">
        <Lock className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-[#0F766E]" />
        Your information is sent over an encrypted connection and is visible only to you and the authorized people listed below.{" "}
        <button onClick={() => navigate("/privacy")} className="underline text-[#1E3E62]" data-testid="member-privacy-link">Privacy &amp; Security</button>
      </p>

      {/* Ask Nauticus */}
      <div className="mt-4 rounded-2xl border-2 border-[#0B192C] bg-[#0B192C] text-white p-5" data-testid="member-ask">
        <div className="flex items-center gap-2 text-[#E9C46A] text-sm font-bold uppercase tracking-wide">
          <MessageSquare className="w-4 h-4" /> Ask Nauticus
        </div>
        <p className="mt-2 text-sm text-white/80">Everything you save here, Nauticus remembers — and anything you tell Nauticus shows up here.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => navigate("/app/ask")} className="rounded-full bg-[#E9C46A] text-[#0B192C] hover:bg-[#f0d98a] font-semibold" data-testid="member-ask-open">
            Open Ask Nauticus <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button onClick={() => navigate("/app/review")} variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10 font-semibold" data-testid="member-start-review">
            Start my Medicare review <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button onClick={() => navigate("/app/ask?compare=1")} variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10 font-semibold" data-testid="member-open-compare">
            Compare 2027 plans <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Current coverage */}
      <Card title="Current Medicare coverage" icon={ShieldCheck} testid="member-coverage">
        {covKnown ? (
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm" data-testid="member-coverage-verified">
            <div><p className="text-xs text-[#6B7280]">Carrier</p><p className="font-semibold text-[#0B192C]">{cov.carrier || "—"}</p></div>
            <div><p className="text-xs text-[#6B7280]">Plan</p><p className="font-semibold text-[#0B192C]">{cov.plan_name || "—"}</p></div>
            <div><p className="text-xs text-[#6B7280]">Plan year</p><p className="font-semibold text-[#0B192C]">{cov.plan_year || "—"}</p></div>
            <div><p className="text-xs text-[#6B7280]">Status</p><p className="font-semibold text-[#2D6A4F]">Confirmed by you</p></div>
          </div>
        ) : (
          <div className="mt-3" data-testid="member-coverage-empty">
            <p className="text-sm text-[#4B5563]">Your current coverage isn't on record yet. Add it and Nauticus can answer using your exact plan — no plan ID numbers needed.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/app/ask")} data-testid="member-coverage-add">
              Add my current coverage <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}
      </Card>

      {/* Enrollment records shared by your advisor */}
      {sunfireDocs.length > 0 && (
        <Card title="Enrollment records" icon={FileText} testid="member-sunfire">
          <p className="text-xs text-[#6B7280] mt-1">Documents your licensed insurance agent shared with you. A submitted application is not confirmation that coverage is active.</p>
          <div className="mt-3 rounded-xl border-2 border-[#E5E5E0] divide-y">
            {sunfireDocs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 p-3" data-testid={`member-sunfire-${d.id}`}>
                <FileText className="w-5 h-5 text-[#0B192C] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0B192C] truncate">{d.original_filename}</p>
                  <p className="text-xs text-[#6B7280] truncate">
                    {[d.plan?.carrier, d.plan?.plan_name].filter(Boolean).join(" · ") || "Sunfire enrollment"} · Submitted application
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => viewSunfire(d)} data-testid={`member-sunfire-view-${d.id}`}><Download className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* My Medicare Review */}
      <Card title="My Medicare review" icon={ClipboardList} testid="member-review">
        {review.objective && <p className="mt-2 text-sm text-[#0B192C]"><span className="text-[#6B7280]">Working on:</span> {review.objective}</p>}
        {candidates.length > 0 ? (
          <div className="mt-3 space-y-2">
            {candidates.map((pl) => (
              <div key={pl.plan_name + (pl.subtitle || "")} className="flex items-center justify-between gap-3 rounded-xl bg-[#F7F7F4] p-3" data-testid="member-review-plan">
                <div className="min-w-0">
                  <p className="font-semibold text-[#0B192C] truncate">{pl.plan_name}</p>
                  <p className="text-xs text-[#6B7280] truncate">{[pl.carrier, pl.subtitle].filter(Boolean).join(" · ")}</p>
                </div>
                <span className="text-[10px] font-semibold text-[#1E3E62] border border-[#1E3E62]/30 rounded-full px-2 py-0.5 shrink-0">In review</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-[#6B7280]" data-testid="member-review-empty">No plans in your review yet. Ask Nauticus to look into a plan and it'll appear here.</p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/app/ask")} data-testid="member-review-continue">Continue my review</Button>
          {home?.comparison_available && (
            <Button size="sm" className="rounded-full bg-[#1E3E62] hover:bg-[#274b73]" onClick={() => navigate("/app/ask?compare=1")} data-testid="member-review-compare">Compare with my current coverage</Button>
          )}
        </div>
      </Card>

      {/* Doctors */}
      <Card title="Doctors that matter to you" icon={Stethoscope} testid="member-doctors">
        <p className="text-xs text-[#6B7280] mb-2" data-testid="member-sensitive-note">
          <Lock className="w-3 h-3 inline -mt-0.5 mr-1 text-[#0F766E]" />
          This is stored privately and used only to help with your Medicare review. Only you and the authorized people on this page can see it.
        </p>
        <p className="text-xs text-[#6B7280] mt-1">Saved as your preference. This is not a network check — a saved doctor doesn't mean in-network.</p>
        <div className="mt-3 flex gap-2">
          <Input value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="e.g. Dr. Grube" data-testid="member-doctor-input" onKeyDown={(e) => e.key === "Enter" && addDoctor()} />
          <Button onClick={addDoctor} className="shrink-0" data-testid="member-doctor-add">Save</Button>
        </div>
        <div className="mt-3 space-y-2">
          {doctors.map((d, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-xl bg-[#F7F7F4] p-3" data-testid="member-doctor-row">
              <p className="font-medium text-[#0B192C] truncate">{d.provider_name || d.health_system}</p>
              <span className="text-[10px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-full px-2 py-0.5 shrink-0">You told us · network not checked</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Prescriptions */}
      <Card title="Your prescriptions" icon={Pill} testid="member-meds">
        <p className="text-xs text-[#6B7280] mt-1">Saved as you reported them. This doesn't confirm any plan covers them or a cost.</p>
        <div className="mt-3 flex gap-2">
          <Input value={medName} onChange={(e) => setMedName(e.target.value)} placeholder="e.g. metformin" data-testid="member-med-input" onKeyDown={(e) => e.key === "Enter" && addMed()} />
          <Button onClick={addMed} className="shrink-0" data-testid="member-med-add">Save</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {meds.map((m, i) => (
            <span key={i} className="text-sm bg-[#F7F7F4] border border-[#E5E7EB] rounded-full px-3 py-1 text-[#0B192C]" data-testid="member-med-chip">
              {m.value} <span className="text-[10px] text-[#6B7280]">· coverage not checked</span>
            </span>
          ))}
        </div>
      </Card>

      {/* What matters */}
      <Card title="What matters to me" icon={Heart} testid="member-priorities">
        {whatMatters.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="member-priorities-list">
            {whatMatters.map((w) => (
              <span key={w.key} className="text-sm bg-[#EAF3EF] text-[#065F46] border border-[#A7F3D0] rounded-full px-3 py-1">{w.label}</span>
            ))}
          </div>
        )}
        <p className="text-xs text-[#6B7280] mt-3">Add what's important — Nauticus keeps this in mind:</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {PRIORITY_CHOICES.filter((c) => !haveKeys.includes(c.key)).map((c) => (
            <button key={c.key} onClick={() => addPriority(c.key)} className="text-sm bg-white border border-[#E5E7EB] hover:border-[#1E3E62] rounded-full px-3 py-1 text-[#1E3E62] transition-colors" data-testid={`member-priority-add-${c.key}`}>
              + {c.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Advisor */}
      <Card title="Talk to a licensed advisor" icon={Handshake} testid="member-advisor">
        {advisor.pending_reason ? (
          <p className="mt-2 text-sm text-[#2D6A4F]" data-testid="member-advisor-pending">A licensed advisor can continue with everything you've saved here — nothing will be lost.</p>
        ) : (
          <p className="mt-2 text-sm text-[#4B5563]">Prefer to talk it through? A licensed insurance agent can pick up with what you've saved in My Nauticus and your review context.</p>
        )}
        <Button variant="outline" size="sm" className="mt-3 rounded-full" onClick={requestAdvisor} data-testid="member-advisor-request">
          {advisor.pending_reason ? "Advisor help requested" : "Ask for a licensed insurance agent"}
        </Button>
        {callCfg?.available && (
          <a href={`tel:${callCfg.business_number}`}
             className="mt-3 ml-0 sm:ml-2 inline-flex items-center gap-2 rounded-full border border-[#1E3E62] px-4 py-1.5 text-sm font-semibold text-[#1E3E62] hover:bg-[#1E3E62] hover:text-white transition-colors"
             data-testid="member-call-advisor">
            <Phone className="w-4 h-4" /> Call my agent
          </a>
        )}
        {callCfg?.text_available && (
          <a href={`sms:${callCfg.business_number}`}
             className="mt-3 ml-0 sm:ml-2 inline-flex items-center gap-2 rounded-full border border-[#1E3E62] px-4 py-1.5 text-sm font-semibold text-[#1E3E62] hover:bg-[#1E3E62] hover:text-white transition-colors"
             data-testid="member-text-advisor">
            <MessageSquare className="w-4 h-4" /> Text my agent
          </a>
        )}
      </Card>

      {/* Privacy — who can access my information */}
      {access && (
        <Card title="Privacy & who can access your information" icon={Users} testid="member-access">
          <ul className="mt-3 space-y-2" data-testid="member-access-entries">
            {access.entries.map((e) => (
              <li key={e.role} className="text-sm" data-testid={`member-access-${e.role}`}>
                <span className="font-semibold text-[#0B192C]">{e.label}</span>
                <span className="text-[#4B5563]"> — {e.detail}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 rounded-lg bg-[#F3F8FF] border border-[#CFE0F5] p-3">
            {access.boundaries.map((b, i) => (
              <p key={i} className="text-xs text-[#1E3E62] flex items-start gap-1.5"><ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />{b}</p>
            ))}
          </div>
          <div className="mt-3">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setReqOpen((v) => !v); setReqStatus(null); }} data-testid="member-access-request-toggle">
              Request an access change
            </Button>
            {reqOpen && (
              <div className="mt-3 space-y-2" data-testid="member-access-request-form">
                <textarea value={reqMsg} onChange={(e) => setReqMsg(e.target.value)} rows={3} maxLength={1000}
                  placeholder="Tell us what you'd like changed (e.g. a different agent). Please don't include any SSN or Medicare number."
                  className="w-full rounded-md border border-[#D1D5DB] p-2 text-sm" data-testid="member-access-request-input" />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={submitAccessRequest} disabled={reqBusy} className="bg-[#0B192C] text-white rounded-full" data-testid="member-access-request-submit">
                    {reqBusy ? "Submitting…" : "Submit request"}
                  </Button>
                  <span className="text-xs text-[#6B7280]">This submits a request — it doesn't change access right away.</span>
                </div>
                {reqStatus && (
                  <p className={`text-sm ${reqStatus.ok ? "text-[#2D6A4F]" : "text-rose-600"}`} data-testid="member-access-request-status">{reqStatus.text}</p>
                )}
              </div>
            )}
          </div>
          <button onClick={() => navigate("/privacy")} className="mt-3 text-sm underline text-[#1E3E62]" data-testid="member-access-privacy-link">Read our Privacy &amp; Security page</button>
        </Card>
      )}
    </div>
  );
}
